import { virtualAssetUrl } from "../offline/virtualUrls";
import { getAsset, getFlight } from "../offline/store";

export interface CachedFlightInfo {
  flightId: string;
  /** True when the download stored preview.mp4 under `:preview`. */
  hasPreview: boolean;
}

export interface VideoSource {
  videoUrl: string;
  manifestUrl: string | undefined;
  previewUrl: string | undefined;
}

/** Decide what the viewer plays. A cached flight always plays its cached rung
 *  from IndexedDB through app-origin virtual URLs the Service Worker serves
 *  with Range support (only one rung is cached, so there is nothing to adapt
 *  between). Uncached HLS flights stream adaptively via hls.js; uncached
 *  progressive flights play their presigned MP4. */
export function chooseVideoSource(
  urls: { video?: string; manifest?: string; preview?: string },
  cached: CachedFlightInfo | null,
  origin: string,
): VideoSource {
  if (cached !== null) {
    return {
      videoUrl: virtualAssetUrl(origin, cached.flightId, "video"),
      manifestUrl: undefined,
      // The scrub preview is a nicety, not part of playback. When one was
      // cached, serve it from IndexedDB; when it wasn't, fall back to the
      // presigned URL so smooth scrubbing still works online (offline it
      // simply fails to load, and the player scrubs on the main video).
      previewUrl: cached.hasPreview
        ? virtualAssetUrl(origin, cached.flightId, "preview")
        : urls.preview,
    };
  }
  return { videoUrl: urls.video ?? "", manifestUrl: urls.manifest, previewUrl: urls.preview };
}

/** Virtual URLs only work when a Service Worker controls the page. */
export function isServiceWorkerControlled(nav: Navigator = navigator): boolean {
  const sw = (nav as { serviceWorker?: { controller?: unknown } }).serviceWorker;
  return sw != null && sw.controller !== null && sw.controller !== undefined;
}

export interface ResolveCachedFlightDeps {
  getFlight: typeof getFlight;
  getAsset: typeof getAsset;
  isControlled: () => boolean;
}

/**
 * Decide whether `flightId` can actually be played from the offline cache.
 *
 * A flight record on its own is not enough evidence. The SW serves virtual
 * URLs straight out of IndexedDB and deliberately 404s a miss even when the
 * browser is online, so every precondition for that path has to hold before
 * the viewer is handed a virtual URL:
 *
 *  - the record exists, and belongs to the team currently being browsed
 *    (cache keys are flightId-only, so an admin browsing `?team=` could
 *    otherwise be served another team's blob);
 *  - the record is not a partial (cancelled/failed) download;
 *  - the `:video` blob is still present (the browser can evict blobs and
 *    leave the record behind);
 *  - a Service Worker controls this page to serve the virtual URLs.
 *
 * Every rejection except "no record at all" — the ordinary state of an
 * uncached flight — is logged, so a dead player has a reason in the console.
 */
export async function resolveCachedFlight(
  flightId: string,
  team: string,
  deps: ResolveCachedFlightDeps = { getFlight, getAsset, isControlled: isServiceWorkerControlled },
): Promise<CachedFlightInfo | null> {
  const record = await deps.getFlight(flightId);
  // No record is the ordinary state of an uncached flight, not an anomaly, so
  // it is the one rejection that stays silent — warning here would log a line
  // on every online viewer load. The four below all mean "this flight looks
  // cached but cannot be played from the cache", which needs explaining.
  if (record === null) return null;
  if (record.team !== team) {
    console.warn(
      `[offline] flight ${flightId} is cached for another team `
      + `("${record.team}", browsing "${team}"); streaming instead`,
    );
    return null;
  }
  if (record.partial === true) {
    console.warn(
      `[offline] flight ${flightId} is a partial download; streaming instead`,
    );
    return null;
  }
  if ((await deps.getAsset(`${flightId}:video`)) === null) {
    console.warn(
      `[offline] flight ${flightId} is cached but its :video blob is gone `
      + "(evicted?); streaming instead",
    );
    return null;
  }
  if (!deps.isControlled()) {
    console.warn(
      `[offline] flight ${flightId} is cached but no Service Worker `
      + "controls this page; streaming instead",
    );
    return null;
  }
  return { flightId, hasPreview: (record.assetCounts.preview ?? 0) === 1 };
}
